import type { Project, ProjectManager } from '../types';

/** Normalize assignment rows: one row per user, prefer flagged primary. */
export function normalizeProjectManagerAssignments(
  assignments: ProjectManager[],
  projectManagerId?: string | null,
): ProjectManager[] {
  const byUserId = new Map<string, ProjectManager>();

  for (const pm of assignments) {
    const uid = pm.userId?.toString();
    if (!uid) continue;
    const existing = byUserId.get(uid);
    if (!existing || pm.isPrimary === true) {
      byUserId.set(uid, pm);
    }
  }

  const normalized = Array.from(byUserId.values());

  if (!normalized.some((pm) => pm.isPrimary === true) && projectManagerId) {
    const primaryId = projectManagerId.toString();
    return normalized.map((pm) =>
      pm.userId?.toString() === primaryId ? { ...pm, isPrimary: true } : pm,
    );
  }

  return normalized;
}

export function resolveProjectManagerDisplay(
  project: Pick<Project, 'managerId' | 'managerName' | 'projectManagers'>,
): {
  primaryName: string;
  primaryUserId: string;
  additionalManagers: ProjectManager[];
} {
  const assignments = normalizeProjectManagerAssignments(
    project.projectManagers ?? [],
    project.managerId,
  );

  if (assignments.length === 0) {
    return {
      primaryName: project.managerName?.trim() || 'Unknown Manager',
      primaryUserId: project.managerId?.toString() ?? '',
      additionalManagers: [],
    };
  }

  let primaryAssignment =
    assignments.find((pm) => pm.isPrimary === true) ??
    (project.managerId
      ? assignments.find((pm) => pm.userId?.toString() === project.managerId.toString())
      : undefined) ??
    assignments[0];

  const primaryUserId = primaryAssignment.userId?.toString() ?? '';
  const primaryName =
    primaryAssignment.name?.trim() ||
    project.managerName?.trim() ||
    'Unknown Manager';

  const additionalManagers = assignments.filter((pm) => {
    const uid = pm.userId?.toString();
    if (!uid || uid === primaryUserId) return false;
    if (pm.isPrimary === true) return false;
    return true;
  });

  return { primaryName, primaryUserId, additionalManagers };
}
